// client/src/pages/ArticleDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ArticleSidebar from '../components/ArticleSidebar';
import MetaTags from '../components/MetaTags';
import '../styles/ArticleDetailPage.css';

// Composants SVG inline
const IconCalendar = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const IconClock = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const IconUser = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const IconBack = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [articlesConnexes, setArticlesConnexes] = useState([]);
  const [papabili, setPapabili] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  
  // Image de fallback pour les articles sans image
  const defaultImage = '/images/article-default.png';
  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setImageError(false);
        
        // Récupérer l'article par son slug
        const response = await axios.get(`/api/articles/slug/${slug}`);
        const { article, articlesConnexes, papabili } = response.data;
        
        setArticle(article);
        setArticlesConnexes(articlesConnexes);
        setPapabili(papabili);
        
        window.scrollTo(0, 0);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement de l\'article:', err);
        setError('Cet article n\'existe pas ou a été supprimé');
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [slug]);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl) => {
    if (!originalUrl || imageError) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = () => {
    console.log("Erreur de chargement d'image, utilisation de l'image par défaut");
    setImageError(true);
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      return format(new Date(dateString), 'EEEE d MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };
  
  // Obtenir la traduction de la catégorie
  const getCategorieLabel = (categorie) => {
    const categories = {
      'actualite': 'Actualité',
      'analyse': 'Analyse',
      'interview': 'Interview',
      'evenement': 'Événement'
    };
    
    return categories[categorie] || categorie;
  };
  
  // Formater le temps de lecture
  const formatTempsLecture = (minutes) => {
    return `${minutes} min${minutes > 1 ? 's' : ''} de lecture`;
  };
  
  if (loading) return <div className="loading">Chargement de l'article...</div>;
  if (error) return (
    <div className="error-container">
      <div className="error-message">{error}</div>
      <button className="go-back-button" onClick={() => navigate('/actualites')}>
        <IconBack /> Retour aux actualités
      </button>
    </div>
  );
  if (!article) return <div className="error">Article non trouvé</div>;
  
  return (
    <>
      {/* Meta tags pour le SEO */}
      <MetaTags 
        title={article.titre}
        description={article.resume}
        image={article.image}
        url={`/actualites/${article.slug}`}
      />
      
      <div className="article-detail-page">
        <div className="article-content-with-sidebar">
          <div className="article-main-content">
            <div className="article-navigation">
              <div className="article-breadcrumbs">
                <Link to="/">Accueil</Link> {' > '}
                <Link to="/actualites">Actualités</Link> {' > '}
                <span className="current-breadcrumb">{article.titre}</span>
              </div>
              
              <Link to="/actualites" className="back-to-articles">
                <IconBack /> Toutes les actualités
              </Link>
            </div>
            
            <article className="article-full">
              <div className="article-header">
                <div className="article-category-badge">
                  {getCategorieLabel(article.categorie)}
                </div>
                
                <h1 className="article-title">{article.titre}</h1>
                
                <div className="article-meta">
                  <div className="meta-item">
                    <IconCalendar /> {formatDate(article.datePublication)}
                  </div>
                  
                  <div className="meta-item">
                    <IconUser /> {article.auteur}
                  </div>
                  
                  <div className="meta-item">
                    <IconClock /> {formatTempsLecture(article.dateLecture || 5)}
                  </div>
                </div>
              </div>
              
              <div className="article-hero-image">
                <img 
                  src={getApiImageUrl(article.image)} 
                  alt={article.titre} 
                  onError={handleImageError}
                />
              </div>
              
              <div className="article-resume">
                <p>{article.resume}</p>
              </div>
              
              <div className="article-body">
                <div className="article-content" dangerouslySetInnerHTML={{ __html: article.contenu }} />
              </div>
              
              <div className="article-footer">
                <div className="article-share">
                  <div className="share-label">Partager cet article :</div>
                  <div className="share-buttons">
                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.titre)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button twitter">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                      </svg>
                    </a>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button facebook">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                      </svg>
                    </a>
                    <a href={`mailto:?subject=${encodeURIComponent(article.titre)}&body=${encodeURIComponent(window.location.href)}`} className="share-button email">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </a>
                  </div>
                </div>
                
                <div className="article-author-info">
                  <div className="author-avatar">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="author-text">
                    <div className="author-name">{article.auteur}</div>
                    <div className="author-role">Rédacteur</div>
                  </div>
                </div>
              </div>
            </article>
          </div>
          
          <ArticleSidebar 
            articlesConnexes={articlesConnexes} 
            papabili={papabili} 
          />
        </div>
      </div>
    </>
  );
};

export default ArticleDetailPage;